import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { AuthenticationService } from '../authentication.service';
import { RecaptchaService } from '../recaptcha.service';
import { WarningPanelComponent } from '../../comps/warning-panel/warning-panel.component';
import { SimpleProgressComponent } from '../../comps/simpleprogress.component';
import { BGAnimationComponent } from '../../comps/bganimation/bganimation.component';
import { AuthShellComponent } from '../auth-shell/auth-shell.component';
import { MaterialModule } from '../../material.module';
import { environment } from '../../../environments/environment';

// Custom validator to check if password and confirm password match
export function passwordMatchValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('password');
    const confirmPassword = control.get('confirmpassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  };
}

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
  imports: [MaterialModule, BGAnimationComponent, AuthShellComponent, FormsModule, ReactiveFormsModule, RouterLink, SimpleProgressComponent, WarningPanelComponent]
})
export class SignUpComponent {

  environment = environment;

  form: UntypedFormGroup;
  error: string;
  busy: boolean;
  registered: boolean;
  invited = false;
  orgName: string;

  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private ngxLogger = inject(NGXLogger);
  private authService = inject(AuthenticationService);
  private recaptchaService = inject(RecaptchaService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    const route = inject(ActivatedRoute);
    this.createForm();
    route.queryParamMap.subscribe(
      params => {
        if (params.get('invitecode')) {
          this.form.controls.inviteCode.setValue(params.get('invitecode'));
          this.form.controls.inviteCode.disable();

          // if we have a code AND email AND org name then assume they are accepting an invite
          if (params.get('email') && params.get('org')) {

            this.form.controls.email.setValue(params.get('email'));
            this.form.controls.email.disable();

            this.orgName = params.get('org');
            this.form.controls.orgname.setValue(this.orgName);
            this.form.controls.orgname.disable();
            this.form.controls.inviteCode.disable();

            this.invited = true;
          }
        }

        this.cdr.markForCheck();
      });
  }

  tryAgain(): void {
    this.registered = false;
  }

  async register() {

    this.error = '';
    this.busy = false;
    if (this.form.valid) {
      this.busy = true;

      try {
        const recaptchaToken = await this.recaptchaService.getToken('signup').catch(err => {
          this.ngxLogger.warn('reCAPTCHA token fetch failed', err);
          return null;
        });

        await this.authService.register(
          // this.form.controls.name.value,
          this.form.controls.email.value,
          this.form.controls.password.value,
          // this.form.controls.phone.value
          this.form.controls.inviteCode.value,
          this.form.controls.orgname.value,
          recaptchaToken
        ).then(result => {

          if (result) {
            // show confirmation code entry
            this.router.navigate(['/confirm'], { queryParams: { user: this.form.controls.email.value } });
            //this.registered = true;
          }
        }).catch(err => {

          this.ngxLogger.error('Error during sign up', err);

          this.error = 'There was an unknown error.  Please try again later.';

          if (err.name == 'UsernameExistsException') {
            this.error = 'You are already registered.  Try to sign in.';
          }

          if (err.name == 'UserLambdaValidationException') {
            this.error = 'Your sign up seems suspicious. Please contact Support for assistance.';
          }

        });

      } finally {
        this.busy = false;
        this.cdr.markForCheck();
      }
    }
  }

  private createForm(): void {
    this.form = this.fb.group({
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      password: new UntypedFormControl('', Validators.required),
      confirmpassword: new UntypedFormControl('', Validators.required),
      inviteCode: new UntypedFormControl(''),
      orgname: new UntypedFormControl('')

    }, { validators: passwordMatchValidator() });
  }
}
